import React from 'react';
import intersection from 'lodash/intersection';
import difference from 'lodash/difference';
import PropTypes from 'prop-types';
import {useSelector, useDispatch} from 'react-redux';

import {Box, Checkbox, Text} from '@spothero/ui';

import Card from 'common/card/Card';
import {
    setFilteredFacilities,
    updateSelectedFacilityId,
} from 'pages/facilities/store/facilities-actions';
import {
    selectAllFilteredFacilities,
    selectFilteredFacilityIds,
    selectSelectedFacilityId,
} from 'pages/facilities/store/facilities-selectors';

import FacilityCardDetails from './FacilityCardDetails';

const canonicalTitleContainerStyles = ({
    areSomeParkingSpotsSelected = false,
    isMultiSelectEnabled = false,
}) => ({
    outline: '1px solid',
    outlineColor: areSomeParkingSpotsSelected ? 'primary.500' : 'gray.200',
    outlineOffset: '-1px',
    borderTopLeftRadius: 4,
    borderTopRightRadius: 4,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    gap: isMultiSelectEnabled ? 1.5 : 0,
    width: '100%',
    height: 12,
    paddingX: 4,
    paddingY: 3,
    position: 'relative',
    zIndex: areSomeParkingSpotsSelected ? 'layer1' : 'base',
    fontWeight: areSomeParkingSpotsSelected ? 'semibold' : 'normal',
});

const FacilityCard = ({
    facility,
    facilityEntities,
    isMultiSelectEnabled,
    showCanonicalGroups,
    style,
}) => {
    const dispatch = useDispatch();
    const filteredFacilities = useSelector(selectAllFilteredFacilities);
    const filteredFacilityIds = useSelector(selectFilteredFacilityIds);
    const selectedFacilityId = useSelector(selectSelectedFacilityId);
    const {id, parkingSpots} = facility;
    const shouldShowCheckbox = parkingSpots?.length > 1 && isMultiSelectEnabled;
    const canonicalTitle = `${facility?.address?.streetAddress}, ${facility?.address?.city}, ${facility?.address?.state} ${facility?.address?.zipcode}`;

    // Handling Canonical facility check
    // determine if the canonical facility is selected
    // all are selected => intersection between parking spot ids and filtered facilities array, if the intersection length is equal then all are selected
    const areAllParkingSpotsSelected =
        intersection(parkingSpots, filteredFacilityIds)?.length ===
        parkingSpots?.length;
    // some are selected => intersection between parking spot ids and filtered facilities array, if the intersection length is greater than 0 but not equal then some are selected
    const areSomeParkingSpotsSelected =
        intersection(parkingSpots, filteredFacilityIds)?.length > 0 &&
        intersection(parkingSpots, filteredFacilityIds)?.length !==
            parkingSpots?.length;

    const handleSelectCanonicalFacility = async () => {
        try {
            // If there is an intersection between the facility entity ids (parkingSpots) and the filtered facility ids arrays, then remove the facility entities from the filtered facilities array
            if (intersection(parkingSpots, filteredFacilityIds)?.length) {
                return dispatch(
                    setFilteredFacilities(
                        difference(filteredFacilityIds, parkingSpots)
                    )
                );
            }

            // If there is no intersection between the facility entity ids (parkingSpots) and the filtered facility ids arrays, then add the facility entities to the filtered facilities array
            await dispatch(
                setFilteredFacilities([...filteredFacilityIds, ...parkingSpots])
            );

            dispatch(updateSelectedFacilityId(parkingSpots[0]));
        } catch (error) {
            console.error('Error selecting facility', error);
        }
    };

    const handleSelectParkingSpot = async (facilityEntity) => {
        try {
            const isFacilitySelected = facilityEntity.id === selectedFacilityId;

            // If multi select is disabled, set the selected facility as the only one in the state. If it's already selected reset the state to an empty array
            if (!isMultiSelectEnabled) {
                await dispatch(
                    setFilteredFacilities(
                        isFacilitySelected ? [] : [facilityEntity.id]
                    )
                );

                return;
            }

            // If multi select is enabled, check if the facility object is present in current state, if present, remove it from the state while still keeping the other selected facilities
            if (
                intersection([facilityEntity.id], filteredFacilityIds)?.length
            ) {
                await dispatch(
                    setFilteredFacilities(
                        difference(filteredFacilityIds, [facilityEntity.id])
                    )
                );

                return;
            }

            // If multi select is enabled, and the facility object is not present in current state, add it to the state along with the other selected facilities
            await dispatch(
                setFilteredFacilities([
                    ...filteredFacilityIds,
                    facilityEntity.id,
                ])
            );

            dispatch(updateSelectedFacilityId(facilityEntity.id));
        } catch (error) {
            console.error('Error selecting facility', error);
        }
    };

    return (
        <Card
            key={id}
            {...style}
            width="fill-available"
            paddingX={{base: 4, tablet: 0}}
            paddingY={1.5}
            marginX={{base: 'auto', tablet: 2}}
        >
            {showCanonicalGroups && (
                <Box
                    {...canonicalTitleContainerStyles({
                        areSomeParkingSpotsSelected:
                            (!isMultiSelectEnabled &&
                                facility.parkingSpots.includes(
                                    selectedFacilityId
                                )) ||
                            (isMultiSelectEnabled &&
                                (areSomeParkingSpotsSelected ||
                                    areAllParkingSpotsSelected)),
                        isMultiSelectEnabled,
                    })}
                >
                    {shouldShowCheckbox && (
                        <Checkbox
                            onChange={handleSelectCanonicalFacility}
                            isChecked={
                                areSomeParkingSpotsSelected ||
                                areAllParkingSpotsSelected
                            }
                            isIndeterminate={
                                !areAllParkingSpotsSelected &&
                                areSomeParkingSpotsSelected
                            }
                            sx={{
                                '.chakra-checkbox__control': {
                                    margin: 0,
                                },
                            }}
                        />
                    )}
                    <Text fontSize="xs" fontWeight="inherit" isTruncated>
                        {canonicalTitle}
                    </Text>
                </Box>
            )}
            <Box position="relative">
                {facilityEntities ? (
                    facilityEntities.map((facilityEntity, index) => {
                        const isSelected = isMultiSelectEnabled
                            ? filteredFacilityIds.includes(facilityEntity.id)
                            : facilityEntity.id === selectedFacilityId;

                        return (
                            <FacilityCardDetails
                                key={index}
                                facilityEntity={facilityEntity}
                                handleSelectParkingSpot={
                                    handleSelectParkingSpot
                                }
                                index={index}
                                isMultiSelectEnabled={isMultiSelectEnabled}
                                isSelected={isSelected}
                                showCanonicalGroups={showCanonicalGroups}
                            />
                        );
                    })
                ) : (
                    <FacilityCardDetails
                        key={id}
                        facilityEntity={facility}
                        handleSelectParkingSpot={handleSelectParkingSpot}
                        isMultiSelectEnabled={isMultiSelectEnabled}
                        isSelected={filteredFacilities.includes(facility)}
                        showCanonicalGroups={showCanonicalGroups}
                    />
                )}
            </Box>
        </Card>
    );
};

FacilityCard.propTypes = {
    facility: PropTypes.shape({
        id: PropTypes.number,
        title: PropTypes.string,
        address: PropTypes.shape({
            streetAddress: PropTypes.string,
            city: PropTypes.string,
            state: PropTypes.string,
            zipcode: PropTypes.string,
        }),
        status: PropTypes.string,
        parkingSpots: PropTypes.arrayOf(PropTypes.number),
    }),
    facilityEntities: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.number,
            title: PropTypes.string,
            status: PropTypes.string,
            productOfferings: PropTypes.arrayOf(PropTypes.string),
            canonicalFacilityId: PropTypes.number,
            operatorFacilityId: PropTypes.string,
            canEditTransientRates: PropTypes.bool,
            canEditEventRates: PropTypes.bool,
            canEditEventTiers: PropTypes.bool,
        })
    ),
    isMultiSelectEnabled: PropTypes.bool.isRequired,
    onSelectedFacility: PropTypes.func,
    showCanonicalGroups: PropTypes.bool,
    style: PropTypes.shape({
        height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        left: PropTypes.number,
        position: PropTypes.string,
        top: PropTypes.number,
        width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    }).isRequired,
};

export default FacilityCard;
